import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useStudioAgreement } from '@brandclub/bc-widgets';
import { getHeadersWithUniqueVisitorId } from '../../Auth';
import type { UIMessage } from '../types/mastraTypes';

export interface AgreementPendingPrompt {
  text: string;
  files?: Array<{ type: 'file'; mediaType: string; url: string }>;
}

export interface ConciergeAgreementGate {
  agreement: ReturnType<typeof useStudioAgreement>;
  pending: AgreementPendingPrompt | null;
  shouldShowCard: boolean;
  isEnabled: boolean;
  hasAccepted: boolean;
  /**
   * Check whether a prompt must be gated. Stashes the prompt and returns true
   * if the send should be blocked. Otherwise returns false and the caller
   * should proceed normally.
   */
  interceptSend: (
    text: string,
    files?: Array<{ type: 'file'; mediaType: string; url: string }>,
  ) => boolean;
  /**
   * Persist acceptance and clear the pending prompt. Returns the previously
   * stashed prompt so the caller can forward it to the chat pipeline.
   */
  accept: () => AgreementPendingPrompt | null;
  /** Clear any stashed prompt without accepting (e.g. on conversation reset). */
  reset: () => void;
}

const COOKIE_PREFIX = 'bc_concierge_agreement_';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const DEFAULT_VERSION = 'v1';

const cookies = new Cookies();

function cookieName(appIdV2: string | null | undefined): string {
  return `${COOKIE_PREFIX}${appIdV2 || 'default'}`;
}

function setAcceptedCookie(appIdV2: string | null | undefined, version: string) {
  cookies.set(cookieName(appIdV2), version, {
    path: '/',
    maxAge: COOKIE_MAX_AGE,
    sameSite: 'lax',
  });
}

async function postAcceptance({
  apiBaseUrl,
  appIdV2,
  version,
}: {
  apiBaseUrl?: string;
  appIdV2: string;
  version: string;
}): Promise<void> {
  if (!apiBaseUrl) return;
  try {
    const headers = await getHeadersWithUniqueVisitorId();
    await fetch(`${apiBaseUrl}/api-rewards/concierge/agreement/accept`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ appIdV2, version }),
    });
  } catch (err) {
    console.warn('[ConciergeAgreementGate] failed to record acceptance to DB', err);
  }
}

async function fetchAcceptanceStatus({
  apiBaseUrl,
  appIdV2,
  version,
}: {
  apiBaseUrl?: string;
  appIdV2: string;
  version: string;
}): Promise<boolean> {
  if (!apiBaseUrl) return false;
  try {
    const headers = await getHeadersWithUniqueVisitorId();
    const url = `${apiBaseUrl}/api-rewards/concierge/agreement/status?appIdV2=${encodeURIComponent(
      appIdV2,
    )}&version=${encodeURIComponent(version)}`;
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) return false;
    const json = (await res.json()) as { accepted?: boolean };
    return !!json.accepted;
  } catch (err) {
    console.warn('[ConciergeAgreementGate] failed to read acceptance from DB', err);
    return false;
  }
}

export interface UseConciergeAgreementGateProps {
  appIdV2: string | null | undefined;
  apiBaseUrl?: string;
  messages: UIMessage[];
  onShown?: () => void;
  onAccepted?: () => void;
}

/**
 * Gate the first concierge message behind a studio-configured agreement.
 *
 * Storage model is hybrid:
 *   - Cookie is the runtime source of truth (fast, no extra request).
 *   - On accept, we also POST to /concierge/agreement/accept so we have a
 *     durable per-user/per-visitor record.
 *   - When the agreement is enabled but the cookie is missing, we make a
 *     best-effort GET to /concierge/agreement/status. If the user has
 *     previously accepted on another device while signed in, we recover the
 *     acceptance and skip the gate.
 */
export function useConciergeAgreementGate({
  appIdV2,
  apiBaseUrl,
  messages,
  onShown,
  onAccepted,
}: UseConciergeAgreementGateProps): ConciergeAgreementGate {
  const agreement = useStudioAgreement();
  const [pending, setPending] = useState<AgreementPendingPrompt | null>(null);
  const [serverAccepted, setServerAccepted] = useState(false);
  // For the 'auto-accept' template: the message is NOT held; the cookie/DB
  // record are written on first send and this flag is flipped to render an
  // informational banner. Close dismisses it (cookie is already saved so it
  // won't reappear next session).
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const shownFiredRef = useRef(false);

  const version = agreement?.version || DEFAULT_VERSION;

  const cookieAccepted = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const stored = cookies.get<string>(cookieName(appIdV2));
    return typeof stored === 'string' && stored === version;
  }, [appIdV2, version]);

  const hasAccepted = cookieAccepted || serverAccepted;

  // Best-effort cross-device recovery: if cookie missing and agreement is
  // enabled, ask the backend whether this subject has already accepted.
  const lookupKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const enabled = !!agreement?.enabled;
    if (!enabled || cookieAccepted || !appIdV2 || !apiBaseUrl) return;
    const lookupKey = `${appIdV2}#${version}`;
    if (lookupKeyRef.current === lookupKey) return;
    lookupKeyRef.current = lookupKey;

    let cancelled = false;
    void fetchAcceptanceStatus({ apiBaseUrl, appIdV2, version }).then((accepted) => {
      if (cancelled) return;
      if (accepted) {
        setAcceptedCookie(appIdV2, version);
        setServerAccepted(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [agreement?.enabled, cookieAccepted, appIdV2, apiBaseUrl, version]);

  // Surface the silent gap where an agreement is enabled but BrandApp V2
  // bootstrap did not provide appIdV2: the gate still works via cookie, but
  // acceptances are never persisted to / recovered from the DB.
  const missingIdWarnedRef = useRef(false);
  useEffect(() => {
    if (!!agreement?.enabled && !appIdV2 && !missingIdWarnedRef.current) {
      missingIdWarnedRef.current = true;
      console.warn(
        '[ConciergeAgreementGate] agreement enabled but appIdV2 is missing — ' +
          'acceptance will be cookie-only (no DB persistence/recovery). ' +
          'Check that BrandApp V2 bootstrap is available for this surface.',
      );
    }
  }, [agreement?.enabled, appIdV2]);

  const isFirstTurn = useMemo(
    () => messages.every((m) => m.role !== 'assistant'),
    [messages],
  );

  const isEnabled = !!agreement?.enabled;
  const shouldGate = isEnabled && !hasAccepted && isFirstTurn;

  const interceptSend = useCallback(
    (text: string, files?: AgreementPendingPrompt['files']) => {
      if (!shouldGate) return false;

      // Auto-accept: don't hold the message. Record acceptance immediately
      // (cookie + DB) and surface an informational banner the shopper can
      // dismiss via Close. The send proceeds normally.
      if (agreement?.template === 'auto-accept') {
        setAcceptedCookie(appIdV2, version);
        if (appIdV2) {
          void postAcceptance({ apiBaseUrl, appIdV2, version });
        }
        onAccepted?.();
        setShowInfoBanner(true);
        return false;
      }

      setPending({ text, files });
      return true;
    },
    [shouldGate, agreement?.template, appIdV2, apiBaseUrl, version, onAccepted],
  );

  const accept = useCallback((): AgreementPendingPrompt | null => {
    // Auto-accept banner: cookie + DB are already written on first send;
    // just dismiss the informational banner.
    if (showInfoBanner) {
      setShowInfoBanner(false);
      shownFiredRef.current = false;
      return null;
    }

    setAcceptedCookie(appIdV2, version);
    if (appIdV2) {
      void postAcceptance({ apiBaseUrl, appIdV2, version });
    }
    const p = pending;
    setPending(null);
    shownFiredRef.current = false;
    onAccepted?.();
    return p;
  }, [appIdV2, apiBaseUrl, version, pending, showInfoBanner, onAccepted]);

  const reset = useCallback(() => {
    setPending(null);
    setShowInfoBanner(false);
    shownFiredRef.current = false;
  }, []);

  useEffect(() => {
    const visible = !!pending || showInfoBanner;
    if (visible && !shownFiredRef.current) {
      shownFiredRef.current = true;
      onShown?.();
    }
    if (!visible) {
      shownFiredRef.current = false;
    }
  }, [pending, showInfoBanner, onShown]);

  return {
    agreement,
    pending,
    shouldShowCard: !!pending || showInfoBanner,
    isEnabled,
    hasAccepted,
    interceptSend,
    accept,
    reset,
  };
}
